import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import { renderMarkdown } from "../lib/markdown";

/**
 * Eine einzelne Datei hinter einem Link — ohne Anmeldung.
 *
 * Das konnten vorher nur die Notizen (`/n/…`). Seit Notizen Dateien sind,
 * muss es das für Dateien geben, sonst wäre die Zusammenlegung ein Verlust.
 *
 * Bewusst getrennt vom öffentlichen Verzeichnis: wer einen Link verschickt,
 * gibt genau diese eine Datei heraus, nicht den Ordner drumherum. Die Seite
 * zeigt deshalb auch nicht, wo die Datei liegt oder was daneben liegt.
 */

type Meta = { name: string; content_type: string; size: number };

const MARKDOWN = /\.(md|markdown|mdown|mdx)$/i;

export function SharedFile() {
  const { token = "" } = useParams();
  const [meta, setMeta] = useState<Meta | null>(null);
  const [text, setText] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const roh = `/api/public/f/${encodeURIComponent(token)}/raw`;

  useEffect(() => {
    let leben = true;
    void (async () => {
      try {
        const r = await fetch(`/api/public/f/${encodeURIComponent(token)}`);
        if (!r.ok) throw new Error(r.status === 404 ? "This link does not exist." : r.statusText);
        const m = (await r.json()) as Meta;
        if (!leben) return;
        setMeta(m);

        // Text holen wir gleich mit; alles andere zeigt der Browser selbst.
        const istText = m.content_type.startsWith("text/")
          || m.content_type === "application/json";
        if (istText) {
          const t = await fetch(roh).then((x) => x.text());
          if (leben) setText(t);
        }
      } catch (e) {
        if (leben) setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { leben = false; };
  }, [token, roh]);

  if (fehler) {
    return (
      <div className="shared-shell">
        <p className="error">{fehler}</p>
      </div>
    );
  }
  if (!meta) {
    return <div className="shared-shell"><p className="muted">Loading…</p></div>;
  }

  const istBild = meta.content_type.startsWith("image/");
  const istPdf = meta.content_type === "application/pdf";
  const istMarkdown = MARKDOWN.test(meta.name) || meta.content_type.startsWith("text/markdown");

  return (
    <div className="shared-shell">
      <header className="shared-kopf">
        <FileText className="icon-svg" />
        <span className="shared-name">{meta.name}</span>
        <a className="ui-btn ui-btn--sm" href={`${roh}?download=1`} title="Download">
          <Download /> Download
        </a>
      </header>

      {istBild ? (
        <img src={roh} alt={meta.name} className="shared-bild" />
      ) : istPdf ? (
        <iframe src={roh} title={meta.name} className="shared-rahmen" />
      ) : text != null ? (
        istMarkdown ? (
          <div className="markdown-body"
               dangerouslySetInnerHTML={{ __html: renderMarkdown(text) }} />
        ) : (
          <pre className="shared-quelltext">{text}</pre>
        )
      ) : (
        <p className="muted">No preview for {meta.content_type || "this type"}.</p>
      )}
    </div>
  );
}
